import React, { useState } from 'react';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useDocumentStore } from '../stores/useDocumentStore';
import { useUIStore } from '../stores/useUIStore';
import { ArrowRight, Sparkles } from 'lucide-react';

export const DocumentGraphView: React.FC = () => {
  const { currentPackage } = useWorkspaceStore();
  const { setActiveDocument } = useDocumentStore();
  const { setActiveTab } = useUIStore();
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  const docs = currentPackage?.documents || [];
  const edges = currentPackage?.dagEdges || [];

  const handleSelectDocNode = (path: string) => {
    setActiveDocument(path);
    setActiveTab('reader');
  };

  return (
    <div className="flex-1 flex flex-col h-[calc(100vh-4rem)] overflow-hidden p-8 max-w-6xl mx-auto space-y-6">
      {/* Graph View Banner */}
      <div className="glass-panel p-6 rounded-xl border border-white/10 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-400" />
            Specification Knowledge Graph (DAG)
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Visual document dependencies and architecture linkages across {currentPackage?.name || 'Workspace'}.
          </p>
        </div>

        <div className="text-xs font-mono px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20">
          {docs.length} Document Nodes • {edges.length} Dependency Edges
        </div>
      </div>

      {/* Main Interactive Graph Viewport */}
      <div className="flex-1 glass-panel rounded-xl border border-white/10 p-8 overflow-auto relative flex flex-col items-center justify-center min-h-[400px]">
        {docs.length === 0 ? (
          <div className="text-xs text-slate-500 font-mono">No documents found to render graph.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full max-w-4xl">
            {docs.map((doc) => {
              const isSelected = selectedNode === doc.path;
              return (
                <div
                  key={doc.path}
                  onClick={() => setSelectedNode(doc.path)}
                  className={`p-5 rounded-xl border transition-all cursor-pointer space-y-3 relative group ${
                    isSelected
                      ? 'bg-blue-600/20 border-blue-500 shadow-lg shadow-blue-500/20 text-white'
                      : 'glass-panel hover:bg-slate-800/60 border-white/10 text-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      #{doc.numericOrder ?? 50}
                    </span>
                    {doc.meta.status && (
                      <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold">
                        {doc.meta.status}
                      </span>
                    )}
                  </div>

                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold truncate text-white">{doc.name}</h3>
                    <p className="text-xs text-slate-400 line-clamp-2">{doc.meta.title}</p>
                  </div>

                  <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[11px] font-mono text-slate-400">
                    <span>{doc.tasks.length} Tasks</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectDocNode(doc.path);
                      }}
                      className="text-blue-400 hover:text-blue-300 flex items-center space-x-1"
                    >
                      <span>Open Doc</span>
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
